import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContentViewer from './ContentViewer';
import AssignmentSubmission from './AssignmentSubmission';
import TestTaking from './TestTaking';
import ProgressRing from './ProgressRing';
import './WeekLearningView.css';

const WeekLearningView = ({ courseId, course, studentId }) => {
  const [currentWeek, setCurrentWeek] = useState(1);
  const [weekProgress, setWeekProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionData, setCompletionData] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [reviewMode, setReviewMode] = useState(false);

  useEffect(() => {
    loadWeekProgress();
  }, [courseId]);

  const loadWeekProgress = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/weeks/flow/${studentId}/${courseId}`);
      const weekData = response.data?.weeks || [];
      setWeekProgress(weekData);
      setReviewMode(Boolean(response.data?.review_mode));

      // Set current week to first unlocked/current week
      const current = weekData.find(w => w.status === 'current') || weekData[0];
      if (current) {
        setCurrentWeek(current.week_number);
      }

      // Load completion data
      const progressRes = await axios.get(`/api/progress/${studentId}/${courseId}`);
      setCompletionData(progressRes.data);
    } catch (error) {
      console.error('Failed to load week progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStatus = (weekNo) => {
    const week = weekProgress.find(w => w.week_number === weekNo);
    return week?.status || 'locked';
  };

  const canAccessWeek = (weekNo) => {
    const status = getWeekStatus(weekNo);
    return status !== 'locked';
  };

  const handleWeekChange = (weekNo) => {
    if (canAccessWeek(weekNo)) {
      setCurrentWeek(weekNo);
      setActiveTab('content');
    }
  };

  const handleContentViewed = () => {
    loadWeekProgress();
  };

  const handleTestComplete = (result) => {
    loadWeekProgress();
  };

  if (loading) return <div className="loading">Loading course...</div>;

  const maxWeeks = course?.duration_weeks || 12;

  return (
    <div className="week-learning-view">
      {/* Week Navigation */}
      <div className="week-navigation">
        <div className="week-nav-header">
          <h3>📚 Learning Path</h3>
          <span className="week-counter">Week {currentWeek} of {maxWeeks}</span>
        </div>

        {reviewMode && (
          <div className="week-info-card" style={{ marginBottom: 12 }}>
            <h4>Review Mode</h4>
            <p>This course is completed. You can still access all course materials.</p>
          </div>
        )}

        <div className="week-selector">
          {weekProgress.slice(0, maxWeeks).map((week) => (
            <button
              key={week.week_number}
              className={`week-nav-item ${week.week_number === currentWeek ? 'active' : ''} week-${week.status}`}
              onClick={() => handleWeekChange(week.week_number)}
              disabled={!canAccessWeek(week.week_number)}
              title={week.title || `Week ${week.week_number}`}
            >
              <span className="week-indicator">
                {week.status === 'completed' && '✓'}
                {week.status === 'current' && '▶'}
                {week.status === 'locked' && '🔒'}
              </span>
              <span className="week-num">W{week.week_number}</span>
            </button>
          ))}
        </div>

        {/* Week Details */}
        {weekProgress.length > 0 && (
          <div className="week-details">
            {(() => {
              const currentWeekData = weekProgress.find(w => w.week_number === currentWeek);
              return currentWeekData ? (
                <div className="week-info-card">
                  <h4>{currentWeekData.title || `Week ${currentWeek}`}</h4>
                  {currentWeekData.description && (
                    <p>{currentWeekData.description}</p>
                  )}
                  <span className={`status-badge status-${currentWeekData.status}`}>
                    {currentWeekData.status === 'completed' && '✓ Completed'}
                    {currentWeekData.status === 'current' && '▶ In Progress'}
                    {currentWeekData.status === 'locked' && '🔒 Locked'}
                  </span>
                </div>
              ) : null;
            })()}
          </div>
        )}
      </div>

      {/* Main Learning Content */}
      <div className="learning-content">
        {!canAccessWeek(currentWeek) ? (
          <div className="week-locked-message">
            <div className="locked-icon">🔒</div>
            <h3>Week {currentWeek} is Locked</h3>
            <p>Complete the previous weeks to unlock this week.</p>
            
            <div className="unlock-requirements">
              <h4>To unlock this week, you must:</h4>
              <ul>
                {weekProgress[currentWeek - 2] && (
                  <>
                    <li>✓ View all materials from Week {currentWeek - 1}</li>
                    <li>✓ Submit the assignment (if available)</li>
                    <li>✓ Pass the test with 50% or more</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        ) : (
          <div className="week-content-tabs">
            {/* Tabs */}
            <div className="content-tabs-header">
              <button
                className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
                onClick={() => setActiveTab('content')}
              >
                📖 Content
              </button>
              <button
                className={`tab-btn ${activeTab === 'assignment' ? 'active' : ''}`}
                onClick={() => setActiveTab('assignment')}
              >
                📝 Assignment
              </button>
              <button
                className={`tab-btn ${activeTab === 'test' ? 'active' : ''}`}
                onClick={() => setActiveTab('test')}
              >
                🧪 Test
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'content' && (
                <ContentViewer
                  courseId={courseId}
                  weekNo={currentWeek}
                  studentId={studentId}
                  onContentViewed={handleContentViewed}
                />
              )}

              {activeTab === 'assignment' && (
                <AssignmentSubmission
                  courseId={courseId}
                  weekNo={currentWeek}
                  studentId={studentId}
                />
              )}

              {activeTab === 'test' && (
                <TestTaking
                  courseId={courseId}
                  weekNo={currentWeek}
                  studentId={studentId}
                  onTestComplete={handleTestComplete}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Progress Summary */}
      {completionData && (
        <div className="progress-summary">
          <h4>📊 Course Progress</h4>
          <div className="progress-ring-wrapper">
            <ProgressRing
              percentage={completionData.progress_percentage || completionData.progress || 0}
              size={100}
              strokeWidth={8}
            />
          </div>
          <span className="progress-label-text">{completionData.progress_percentage || completionData.progress || 0}% Complete</span>
        </div>
      )}
    </div>
  );
};

export default WeekLearningView;
